/**
 * Transforms frontend Event payloads into shapes accepted by the backend API.
 * All field-mapping, URL normalisation, and DTO whitelisting live here so that
 * eventsApi stays focused on HTTP calls only.
 */

import { config } from '../../config/env';
import type {
  EventLocation,
  EventBudget,
} from '../../types/events-unified';
import type { CreateEventRequest, UpdateEventRequest } from '../api/events.api';

// ---------------------------------------------------------------------------
// Whitelist of keys accepted by the backend CreateEventDto / UpdateEventDto.
// Any key not in this set is stripped before the request is sent so that
// NestJS ValidationPipe (forbidNonWhitelisted) does not reject the request.
// ---------------------------------------------------------------------------

// O-24 (18.09.2026): `isRecurring` и `recurrencePattern` из списка сняты.
// Контракт бэкенда этих полей больше не объявляет — запрос с ними получает 400
// от `forbidNonWhitelisted`, а до того они молча терялись при записи события
// (повтор никуда не сохранялся). Повторяемость как настоящая фича серий —
// `AD-380`; до неё ни одна форма их не задаёт.
//
// AD-389 (19.09.2026): `isVirtual` заменён на `format`. Булево выражало два
// состояния из трёх и гибрид назвать не умело; на сервере колонка снята
// миграцией 1790500000000, а `CreateEventDto` объявляет `format`. Мастер это
// поле собирал и раньше — список его выбрасывал, и выбор формата до домена
// не доезжал вовсе.
//
// AD-527 (25.09.2026, решение №41): `timezone` — пояс события, имя IANA. Тип
// `CreateEventRequest`/`UpdateEventRequest` поле объявлял и раньше, но список
// его срезал, и сервер пояса не получал. Теперь его объявляют все три DTO
// бэкенда: по поясу считаются дни, в которых допустим пункт программы.
const CREATE_EVENT_DTO_KEYS = new Set([
  'title', 'description', 'shortDescription', 'startDate', 'endDate', 'timezone', 'location', 'type', 'subType',
  'status', 'visibility', 'tags', 'agenda', 'metadata', 'participantIds', 'isAllDay', 'address',
  'virtualLink', 'format', 'maxParticipants', 'registrationEnabled', 'registrationDeadline',
  'categories', 'coverImage', 'images', 'attachments',
  'hasNotifications', 'notifyBefore', 'color', 'price', 'currency', 'contactEmail', 'contactPhone',
  'website', 'instructions', 'requirements', 'included', 'notIncluded', 'difficultyLevel',
  'minAge', 'maxAge', 'dressCode', 'language', 'isAccessible', 'photoVideoAllowed', 'hasLiveStream',
  'liveStreamUrl', 'targetAudience',
]);

// AD-522 (25.09.2026): состав дверей РАЗОШЁЛСЯ. `agenda` объявлена только в
// `CreateEventDto`: `POST /events` сеет пункты повестки в той же транзакции, а
// `PATCH /events/:id` поле снял (AD-500) — правка программы идёт дверями
// `/events/:id/agenda`. Пока список был один на оба маршрута, ключ, принятый
// одной дверью и не принятый второй, означал 400 ровно на одном из них:
// `forbidNonWhitelisted` роняет не поле, а ВЕСЬ запрос.
//
// Имена этого списка выбрасываются на двери правки и остаются на создании.
// Гейт `scan:create-event-dto` (AD-391) сверяет состав по обеим дверям и
// краснеет, если здесь окажется имя, которое `UpdateEventDto` ПРИНИМАЕТ
// (молчаливая потеря на правке, класс `AD-375`), либо имя, которого нет в
// списке выше (мёртвое исключение — область гейта сузилась молча).
const UPDATE_DOOR_EXCLUDED_KEYS = new Set(['agenda']);

/**
 * Дверь, для которой собирается тело. Параметр обязателен НАМЕРЕННО: молчаливый
 * дефолт `'create'` отправил бы `agenda` на правку и уронил бы весь запрос, а
 * дефолт `'update'` молча терял бы повестку при создании. Пусть компилятор
 * спрашивает дверь у каждого нового вызова.
 */
export type EventPayloadDoor = 'create' | 'update';

// ---------------------------------------------------------------------------
// URL helpers
// ---------------------------------------------------------------------------

/**
 * Backend @IsUrl() rejects relative paths like /uploads/...
 * Prepend the backend origin when the URL is not already absolute.
 */
export function ensureAbsoluteMediaUrl(url: string): string {
  const u = String(url).trim();
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) return u;
  const base = (config.BACKEND_URL || 'http://localhost:3001').replace(/\/$/, '');
  return u.startsWith('/') ? `${base}${u}` : `${base}/${u}`;
}

/**
 * Returns true for URL values that must be stripped before sending to the
 * backend (empty string, blob: object URLs, data: URIs).
 */
export function isDisallowedMediaRef(url: string): boolean {
  const u = String(url).trim().toLowerCase();
  return u === '' || u.startsWith('blob:') || u.startsWith('data:');
}

function isHttpUrlString(url: string): boolean {
  try {
    const p = new URL(url);
    return p.protocol === 'http:' || p.protocol === 'https:';
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

/**
 * Coerces any date-like value to an ISO 8601 string.
 * Returns undefined when the value is absent or not parseable.
 */
export function normalizeOptionalIso8601(value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (value instanceof Date) {
    const iso = value.toISOString();
    return isNaN(Date.parse(iso)) ? undefined : iso;
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? undefined : d.toISOString();
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Main payload builder
// ---------------------------------------------------------------------------

/**
 * AD-306. Собирает адрес одной строкой — так его хранит бэкенд (`location` —
 * одна колонка varchar).
 *
 * Прежде четыре поля склеивались через запятую напрямую, и адрес УДВАИВАЛСЯ на
 * каждом сохранении правки: 1 → 2 → 4 → 8. Причина в том, что поля не
 * независимы. Событие, загруженное на правку, приходит с бэкенда строкой, и
 * форма кладёт её в `name`; `formatted_address` от Google, попадающий в
 * `address`, сам по себе уже содержит город и страну.
 *
 * Поэтому склеиваются не поля, а их части, разделённые запятыми, и часть,
 * которая уже встречалась, отбрасывается. Сравнение — по обрезанной строке без
 * учёта регистра; в результат попадает первое написание, порядок полей
 * (площадка, улица, город, страна) сохраняется.
 */
function formatLocationString(location: EventLocation): string {
  const seen = new Set<string>();
  const parts: string[] = [];
  const fields: unknown[] = [
    location.name,
    location.address,
    location.city,
    location.country,
  ];

  for (const field of fields) {
    if (typeof field !== 'string') continue;
    for (const part of field.split(',')) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      parts.push(trimmed);
    }
  }

  return parts.join(', ');
}

/**
 * Converts a CreateEventRequest or UpdateEventRequest into the exact payload
 * shape expected by the backend (POST /events or PATCH /events/:id).
 *
 * - Strips keys not in the DTO whitelist (avoids 400 from forbidNonWhitelisted)
 * - Serialises Date objects to ISO strings
 * - Expands `location` object to a formatted address string
 * - Filters out blob:/data: image refs and ensures absolute URLs
 * - Excludes `budget` (submitted separately via createEventBudget)
 */
export function buildEventPayload(
  data: CreateEventRequest | UpdateEventRequest,
  door: EventPayloadDoor
): Record<string, unknown> {
  const {
    location,
    images,
    budget: _budget,
    agenda,
    category,
    ...rest
  } = data as CreateEventRequest & { budget?: EventBudget; category?: string };

  const raw: Record<string, unknown> = { ...rest };

  // category → categories array (backend field name)
  if (category !== undefined && category !== '') {
    raw['categories'] = [category];
  }

  // location: object → formatted string
  if (location !== undefined) {
    raw['location'] =
      typeof location === 'string'
        ? location
        : formatLocationString(location as EventLocation);
  }

  // images: string[] → EventImageDto[]
  if (images !== undefined) {
    if (Array.isArray(images)) {
      const mapped = (images as string[])
        .filter((url) => url != null && !isDisallowedMediaRef(String(url)))
        .map((url, idx) => ({
          url: ensureAbsoluteMediaUrl(String(url)),
          alt: undefined,
          isCover: idx === 0,
        }))
        .filter((img) => isHttpUrlString(img.url));
      if (mapped.length > 0) {
        raw['images'] = mapped;
      } else {
        delete raw['images'];
      }
    } else {
      raw['images'] = images;
    }
  }

  // AD-500. `agenda` — плоский массив `CreateAgendaItemDto` (title, startTime,
  // endTime, type, order, description). Сервер принимает его ТОЛЬКО на
  // создании: `POST /events` сеет пункты в `event_agenda_items` в той же
  // транзакции (`Event.agendaItems`, `cascade: true`). `PATCH /events/:id`
  // поле снял, и лишний ключ роняет запрос в 400 (`forbidNonWhitelisted`) —
  // поэтому правка программы идёт дверями `/events/:id/agenda`, а не здесь.
  // Прежняя ветка собирала jsonb-пункт (`speaker`, `duration` вместо
  // `endTime`) — этой формы в контракте больше нет.
  if (Array.isArray(agenda)) {
    raw['agenda'] = agenda;
  }

  // Dates → ISO strings
  const startVal = raw['startDate'];
  const endVal = raw['endDate'];
  if (startVal instanceof Date) raw['startDate'] = startVal.toISOString();
  if (endVal instanceof Date) raw['endDate'] = endVal.toISOString();

  // registrationDeadline — strip if invalid
  const regNorm = normalizeOptionalIso8601(raw['registrationDeadline']);
  if (regNorm === undefined) {
    delete raw['registrationDeadline'];
  } else {
    raw['registrationDeadline'] = regNorm;
  }

  // coverImage — strip blob/data, ensure absolute URL
  const cover = raw['coverImage'];
  if (
    cover === '' ||
    cover === undefined ||
    (typeof cover === 'string' && isDisallowedMediaRef(cover))
  ) {
    delete raw['coverImage'];
  } else if (typeof cover === 'string' && cover.trim() !== '') {
    const abs = ensureAbsoluteMediaUrl(cover);
    if (isHttpUrlString(abs)) {
      raw['coverImage'] = abs;
    } else {
      delete raw['coverImage'];
    }
  }

  // AD-389 шаг 4. Ссылка на онлайн-часть: ПУСТОЕ поле значит «не трогать», а не
  // «стереть». В ответе события ссылки больше нет ни у кого (`@Exclude` на
  // сущности), мастер правки узнаёт её отдельным запросом, и если тот запрос не
  // состоялся, поле останется пустым — отправив из него пустую строку, правка
  // описания стёрла бы адрес уже назначенной встречи у всех участников.
  // Непустое значение уезжает как есть: у колонки на бэкенде `@IsUrl`, и
  // молча выбросить непохожее на ссылку значило бы сделать вид, что сохранили.
  const virtualLink = raw['virtualLink'];
  if (typeof virtualLink === 'string') {
    const trimmed = virtualLink.trim();
    if (trimmed === '') {
      delete raw['virtualLink'];
    } else {
      raw['virtualLink'] = trimmed;
    }
  } else if (virtualLink === null || virtualLink === undefined) {
    delete raw['virtualLink'];
  }

  // Strip keys not in the DTO whitelist
  const payload: Record<string, unknown> = {};
  for (const key of Object.keys(raw)) {
    if (!CREATE_EVENT_DTO_KEYS.has(key)) continue;
    // AD-522: дверь правки принимает не тот же состав, что дверь создания.
    if (door === 'update' && UPDATE_DOOR_EXCLUDED_KEYS.has(key)) continue;
    payload[key] = raw[key];
  }
  return payload;
}
